// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import isUndefined from 'lodash/isUndefined';
import isNumber from 'lodash/isNumber';
import PropTypes from 'prop-types';

import { Button, makeStyles } from '@material-ui/core';

import { getI18n } from "../../../utils/intl";
import { CHART_HLE_HITS, CHART_HLE_LATENCY, CHART_HLE_ERROR_RATE } from '../../../constants/charts';
import { USER_FILTER_METRICS, USER_FILTER_DATE } from '../../../constants/filter';
import { REPORT_HLE } from '../../../constants/reports';
import Footer from '../../Footer';

import { getAggregation, getStartAndEndRangeForLastNDays, convertWeekRangeToCustom } from '../utils';
import { decodeMetricsFilter, decodeDateFilter,
  getShorthandDuration, getSelectedFilters,
  getSummaryFromMetricsFilters, getSummaryFromDateFilter,
} from '../utils/filter';

import FilterWrapper from '../FilterWrapper';
import CustomDateRangeFilter from '../CustomDateRangeFilter';

import styles from './styles';

const timerangeMap = {
  HOUR_1: 'hour',
  HOUR_24: 'day',
  DAY_7: 'week',
};

const getDateParam = (dateObj) => {
  let aggregation;
  let timerange = 'custom';
  const newParams = {};
  if (!isUndefined(dateObj.customStartDate) && !isUndefined(dateObj.customEndDate)) {
    newParams.startDate = dateObj.customStartDate.format('YYYY-MM-DD');
    newParams.endDate = dateObj.customEndDate.format('YYYY-MM-DD');
    aggregation = getAggregation(timerange, dateObj.customStartDate,
      dateObj.customEndDate);
  } else if (!isUndefined(dateObj.last) && isNumber(dateObj.last)) {
    const customDays = getStartAndEndRangeForLastNDays(dateObj.last);
    newParams.startDate = customDays.startDate;
    newParams.endDate = customDays.endDate;
    aggregation = getAggregation(timerange, customDays.startDate, customDays.endDate);
  } else {
    timerange = timerangeMap[dateObj.last];
    aggregation = getAggregation(timerange);
  }
  newParams.timerange = timerange;
  newParams.aggregation = aggregation;
  return newParams;
}

const Report = (props) => {
  const {
    userId,
    orgUuid,
    type,
    charts,
    apiMap,
    appMap,
    orgMap,
    selectedMetrics,
    selectedDate,
    isDeveloper,
    isOrgAdmin,
    fetchMetricsData,
    setUserMetricFilter,
    setUserDateFilter,
    hideOrgFilterForOrgPub,
    handleSearchFilterNameChange,
    onEntityTypeChange,
  } = props;

  const intl = getI18n(useIntl());
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const defaultParams = {
    timerange: 'week',
    aggregation: 'by-day',
    sortBy: 'date',
    sortOrder: 'desc',
    limit: [3],
    stats: [],
    buckets: ['apis'],
    startDate: '',
    endDate: '',
  }

  const [params, setParams] = useState();

  useEffect(() => {
    const metrics = decodeMetricsFilter(selectedMetrics);
    const metricParams = getSelectedFilters(metrics);

    const dateObj = decodeDateFilter(selectedDate);
    const dateParam = getDateParam(dateObj);

    setParams(currentParams => {
      return { ...defaultParams, ...currentParams, ...{ ...metricParams, ...dateParam } };
    });
  }, []);

  useEffect(() => {
    if (params) {
      handleSelectionChange();
    }
  }, [params]);

  const handleSelectionChange = () => {
    let currentParams = { ...params };
    // DE436065: Using custom dates instead of week,
    // as it represents UI option 'last 7 days' accurately
    currentParams = convertWeekRangeToCustom(currentParams, false);
    currentParams.stats = [];
    fetchMetricsData(CHART_HLE_HITS, currentParams, 'hits', currentParams.aggregation);
    currentParams.stats = ['avg', 'max', 'min'];
    fetchMetricsData(CHART_HLE_LATENCY, currentParams, 'latency', currentParams.aggregation);
    currentParams.stats = ['errorRate'];
    fetchMetricsData(CHART_HLE_ERROR_RATE, currentParams, 'errors', currentParams.aggregation);
  };

  const handleDateRangeChange = (customDateRange) => {
    const timerange = !customDateRange.customDays ? customDateRange.unit : 'custom';
    const daysObject = !isUndefined(customDateRange.customDates) ?
      customDateRange.customDates : getStartAndEndRangeForLastNDays(customDateRange.value);
    const aggregation = getAggregation(timerange, daysObject.startDate, daysObject.endDate);

    const updatedParams = {
      ...params,
      timerange,
      aggregation,
    };
    if (timerange === 'custom') {
      updatedParams.startDate = daysObject.startDate;
      updatedParams.endDate = daysObject.endDate;
    }
    setParams(updatedParams);

    if (!isUndefined(customDateRange.customDates)) {
      updateDateFilter({
        start: daysObject.startDate,
        end: daysObject.endDate,
      });
    } else {
      updateDateFilter({ last: getShorthandDuration(customDateRange) });
    }
  };

  const handleFiltersChange = (selectedFilters) => {
    const updatedParams = {
      ...params,
      limit: selectedFilters.limit,
      buckets: selectedFilters.buckets,
      apiIds: selectedFilters.apiIds,
      appIds: selectedFilters.appIds,
      orgIds: selectedFilters.orgIds,
    };
    setParams(updatedParams);
  }

  const handleExport = (e) => {
    window.print();
    e.preventDefault();
    e.target.blur();
    return false;
  };

  const updateMetricFilters = (filtersState) => {
    setUserMetricFilter(userId, orgUuid, USER_FILTER_METRICS, filtersState);
  }

  const updateDateFilter = (dateObj) => {
    setUserDateFilter(userId, orgUuid, USER_FILTER_DATE, dateObj);
  }

  const renderChart = (obj) => {
    const { component: Component, id } = obj;
    return (
      <div className={classes.printchart} key={id}>
        <Component key={id} />
      </div>
    );
  };

  const renderDataFilterSummary = () => {
    const summaryTextLines = getSummaryFromMetricsFilters(params, {
      apiMap,
      appMap,
      orgMap,
    }, intl);
    return summaryTextLines
      ? <div className={classes.printdataContainer}>
          {summaryTextLines.map((line, index) =>
            <div key={index} className={classes.printdatafilterelem}>{line}</div>,
          )}
        </div>
      : null;
  };

  const renderDateFilterSummary = () => {
    const decodedDateFilter = decodeDateFilter(selectedDate);
    const dateObj = getDateParam(decodedDateFilter);
    const dateFilterSummary = getSummaryFromDateFilter(dateObj, intl);
    return (<div className={classes.printdateContainer}>
      <div className={classes.printdatefilter} >
        {intl.getI18nMessage('label.analyticsDashboard.filter.dateRange.label')}
      </div>
      <div>{dateFilterSummary}</div>
    </div>);
  };

  
  return (
    <div>
      <div className={classes.dashboardContainer}>
        <div className={classes.dashboard}>
        <div className={classes.dashboardLabelContainer}>
          <div className={classes.dashboardLabel}>
            <div className={classes.dashboardLabelTitle}>
              {intl.getI18nMessage('label.analytics.dashboard.title.text')}
            </div>
            <div className={classes.placeholderText}>
              {intl.getI18nMessage('label.analytics.dashboard.placeholder.text')}
            </div>
          </div>
          <div className={classes.exportBtnContainer}>
            <Button className={classes.exportBtn} onClick={handleExport} >
              {intl.getI18nMessage('label.analytics.print.button.export')}
            </Button>
          </div>
        </div>
        <div className={'SkipPrint'}>
          <FilterWrapper
            apiList={apiMap}
            appList={appMap}
            orgList={orgMap}
            handleFiltersChange={handleFiltersChange}
            updateMetricFilters={updateMetricFilters}
            selectedMetrics={decodeMetricsFilter(selectedMetrics)}
            isDeveloper={isDeveloper}
            isOrgAdmin={isOrgAdmin}
            hideOrgFilterForOrgPub={hideOrgFilterForOrgPub}
            handleSearchFilterNameChange={handleSearchFilterNameChange}
            onEntityTypeChange={onEntityTypeChange}
          />
          <CustomDateRangeFilter
            reportType={REPORT_HLE}
            handleDateRangeChange={handleDateRangeChange}
            selectedDate={decodeDateFilter(selectedDate)}
          />
        </div>
        <div className={classes.printfiltersummary}>
          {renderDataFilterSummary()}
          {renderDateFilterSummary()}
        </div>
        {type === REPORT_HLE &&
          <div className={classes.report}>
            {charts && charts.map(renderChart)}
          </div>
        }
        </div>
      </div>
      <Footer />
    </div>
  );
}

Report.propTypes = {
  type: PropTypes.string,
  charts: PropTypes.arrayOf(PropTypes.shape({
    component: PropTypes.object,
    id: PropTypes.string,
  })),
  fetchMetricsData: PropTypes.func,
  apiMap: PropTypes.object,
  appMap: PropTypes.object,
  orgMap: PropTypes.object,
  userId: PropTypes.string,
  orgUuid: PropTypes.string,
  setUserMetricFilter: PropTypes.func,
  setUserDateFilter: PropTypes.func,
  selectedMetrics: PropTypes.arrayOf(PropTypes.object),
  selectedDate: PropTypes.object,
  isDeveloper: PropTypes.bool,
  isOrgAdmin: PropTypes.bool,
  hideOrgFilterForOrgPub: PropTypes.bool,
  handleSearchFilterNameChange: PropTypes.func,
  onEntityTypeChange: PropTypes.func,
}

export default Report;